import * as Yup from 'yup';
import YupPassword from 'yup-password';
import { useAppSelector } from 'store';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTrackingAnalytics } from 'context/trackingAnalytics/provider';
import TagManager from 'react-gtm-module';

import { FormInput, Info } from 'components';
import { RegisterByInvitationRequest, RegisterRequest } from 'types';
import { InviteData, selectInvite } from 'store/invite';

import styles from './RegisterProfileInfoStep.module.scss';

YupPassword(Yup);

type RegisterProfileInfoStepProps = {
  onRegister(model: RegisterByInvitationRequest): Promise<void>;
};

export function RegisterProfileInfoStep({ onRegister }: RegisterProfileInfoStepProps) {
  const { t } = useTranslation('auth');
  const invite: InviteData = useAppSelector(selectInvite)!;
  const trackEvent = useTrackingAnalytics();
  const schema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string()
          .max(50, t('validationMaxLength', { count: 50 }))
          .required(t('validationRequired')),
        lastName: Yup.string()
          .max(50, t('validationMaxLength', { count: 50 }))
          .required(t('validationRequired')),
        email: Yup.string().email(t('validationEmailInvalid')).required(t('validationEmailRequired')),
        organization: Yup.string(),
        password: Yup.string()
          .min(8, t('validationPasswordComplexity'))
          .minLowercase(1, t('validationPasswordLowercase'))
          .minUppercase(1, t('validationPasswordUppercase'))
          .minNumbers(1, t('validationPasswordNumber'))
          .minSymbols(1, t('validationPasswordSymbol'))
          .required(t('validationPasswordRequired')),
        passwordConfirmation: Yup.string().oneOf([Yup.ref('password')], t('validationPasswordsMustMatch')),
      }),
    [t],
  );

  const form = useForm<RegisterRequest>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: invite.firstName,
      lastName: invite.lastName,
      organization: invite.organizationName,
      email: invite.email,
    },
    mode: 'all',
  });

  const handleSubmit = async (): Promise<void> => {
    const model: RegisterByInvitationRequest = {
      invitationId: invite.invitationId,
      firstName: form.getValues().firstName,
      lastName: form.getValues().lastName,
      password: form.getValues().password,
      passwordConfirmation: form.getValues().passwordConfirmation,
    };

    await onRegister(model);
    (window as any).fbq('track', 'Conversion'); // Fire the Facebook conversion event on success
    TagManager.dataLayer({ dataLayer: { event: 'conversion' } });
  };

  return (
    <div className={styles.form}>
      <div className={styles.rowWithTwoInputs}>
        <Stack className={styles.row}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography component='p' variant='body2'>
              {t('registerFirstName')} <span className={styles.asterisk}>*</span>
            </Typography>
          </Stack>
          <FormInput name='firstName' control={form.control} />
        </Stack>

        <Stack className={styles.row}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography component='p' variant='body2'>
              {t('registerLastName')} <span className={styles.asterisk}>*</span>
            </Typography>
          </Stack>
          <FormInput name='lastName' control={form.control} />
        </Stack>
      </div>

      <Stack className={styles.row}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography component='p' variant='body2'>
            {t('emailLabel')} <span className={styles.asterisk}>*</span>
          </Typography>
        </Stack>
        <FormInput name='email' control={form.control} disabled />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography component='p' variant='body2'>
            {t('providerOrgName')} <span className={styles.asterisk}>*</span>
          </Typography>
          <Info text={t('providerOrgInfo')} />
        </Stack>
        <FormInput name='organization' control={form.control} disabled />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography component='p' variant='body2'>
            {t('passwordLabel')} <span className={styles.asterisk}>*</span>
          </Typography>
          <Info text={t('passwordRequirementInfo')} />
        </Stack>
        <FormInput name='password' control={form.control} type='password' />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography component='p' variant='body2'>
            {t('confirmPassword')} <span className={styles.asterisk}>*</span>
          </Typography>
        </Stack>
        <FormInput name='passwordConfirmation' control={form.control} type='password' />
      </Stack>

      <Stack className={styles.row} alignItems='flex-end'>
        <Button
          id='RegisterProfileInfoStepNext'
          variant='contained'
          onClick={handleSubmit}
          disabled={!form.formState.isValid}
          className={styles.formButton}
          type='submit'
        >
          {t('next')}
        </Button>
      </Stack>
    </div>
  );
}
